import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  Circle,
  Clock,
  Sparkles,
  Users,
  Heart,
  MapPin,
  Shield,
  Dna,
  FileText,
  ShoppingBag,
  Briefcase,
  Trophy
} from 'lucide-react';

const FeatureRoadmap = () => {
  const completedFeatures = [
    { name: 'User Authentication & Profiles', icon: Users },
    { name: 'Multi-Pet Support (8 types)', icon: Heart, note: 'Dogs, Cats, Birds, Fish, Reptiles, Small Pets, Horses, Others' },
    { name: 'Social Feed (Posts/Comments)', icon: FileText },
    { name: 'Friends System', icon: Users },
    { name: 'Messaging', icon: FileText },
    { name: 'Dog Parks with Check-in', icon: MapPin, note: '34 real US parks, "who\'s here" tracking' },
    { name: 'Services Directory', icon: Briefcase, note: 'Angi\'s List style with reviews' },
    { name: 'Organizations & Clubs', icon: Users, note: '12 org types, memberships' },
    { name: 'Events', icon: Clock },
    { name: 'Marketplace', icon: ShoppingBag },
    { name: 'Partner Deals System', icon: Sparkles, note: 'Promo codes, redemption tracking' },
    { name: 'Election System', icon: Trophy, note: 'Board elections, nominations, voting' },
    { name: 'Private Forums (Premium)', icon: Shield, note: '8 professional groups, 24 categories' },
    { name: 'Membership Tiers', icon: Users, note: 'Free, Full Member ($5.99), Premium ($14.99)' },
    { name: 'Community Recommendations', icon: Heart, note: 'Upvote/downvote, 7 categories' },
    { name: 'My Pet Care Team', icon: Heart, note: '14 provider types' },
    { name: 'Genetic Health Checklist', icon: Dna, note: '25+ conditions, breed-specific tests' },
    { name: 'Location & Distance Search', icon: MapPin, note: 'All listing pages' },
    { name: 'Interactive Map View', icon: MapPin, note: 'Parks, Services, Events, Organizations' },
    { name: 'Report/Block UI', icon: Shield, note: 'On posts, comments, profiles' },
    { name: 'i18n Support (6 languages)', icon: FileText, note: 'EN, ES, DE, FR, PT, JA' },
    { name: 'FAQ Page', icon: FileText, note: '40+ questions, 10 categories' },
    { name: 'Guest Public Access', icon: Users, note: '8 public categories' },
    { name: 'Medical Records with Encryption', icon: FileText, note: 'AES-256 encryption, privacy controls, selective sharing' },
    { name: 'Pet-to-Pet Friendships', icon: Heart, note: 'Friend requests, suggestions from owner friends' },
    { name: 'Services to Care Team Link', icon: Heart, note: 'Add services directly to your care team' },
    { name: 'Pet Image Upload', icon: FileText, note: 'Multiple image uploads, 5MB limit, gallery support' },
    { name: 'Matchmaking/Breeding', icon: Heart, note: 'Compatibility scoring, match requests, genetic analysis' },
    { name: 'Ancestry/Family Tree', icon: Dna, note: '3-generation pedigree, offspring tracking, siblings' },
    { name: 'Job Board', icon: Briefcase, note: 'Post jobs, apply, track applications' },
  ];

  const inProgressFeatures = [
    { name: 'Pet-type specific breed databases', note: 'Cat breeds, bird species, etc.', priority: 'medium' },
  ];

  const plannedFeatures = [
    { name: 'Consumer Reports', note: 'Compare pet foods & medications', priority: 'low' },
    { name: 'Enhanced Media Albums', note: 'Photos, notes, audio, video, awards', priority: 'low' },
    { name: 'Breeder Promotional Albums', note: 'Professional galleries', priority: 'low' },
    { name: 'Animal Behavior Recording', note: 'Track and log behaviors', priority: 'low' },
    { name: 'Vet/Store Reviews Expansion', note: 'Extended review system', priority: 'low' },
    { name: 'Sports/Competition Tools', note: 'Sponsoring and organizing', priority: 'low' },
    { name: 'Art/Poetry/Stories', note: 'Share and monetize content', priority: 'low' },
    { name: 'Proactive Health Detection', note: 'AI-powered health alerts', priority: 'low' },
    { name: 'Non-allergenic Genetics', note: 'Allergy-focused tools', priority: 'low' },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-heading font-medium mb-4">Feature Roadmap</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Track what's been built, what's in progress, and what's coming next to AADC.
          </p>
          <div className="flex justify-center gap-4 mt-6 flex-wrap">
            <Badge variant="outline" className="px-3 py-1">
              <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
              {completedFeatures.length} Completed
            </Badge>
            <Badge variant="outline" className="px-3 py-1">
              <Clock className="w-4 h-4 mr-1 text-amber-600" />
              {inProgressFeatures.length} In Progress
            </Badge>
            <Badge variant="outline" className="px-3 py-1">
              <Circle className="w-4 h-4 mr-1 text-blue-600" />
              {plannedFeatures.length} Planned
            </Badge>
          </div>
        </div>

        {/* Completed Features */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-6 h-6" />
              Completed Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {completedFeatures.map((feature) => (
                <div 
                  key={`completed-${feature.name}`}
                  className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50 border border-green-100"
                >
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-900">{feature.name}</p>
                    {feature.note && (
                      <p className="text-sm text-green-700/70">{feature.note}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="w-6 h-6" />
              In Progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {inProgressFeatures.map((feature) => (
                <div 
                  key={`progress-${feature.name}`}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-amber-50/50 border border-amber-100"
                >
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-900">{feature.name}</p>
                      {feature.note && (
                        <p className="text-sm text-amber-700/70">{feature.note}</p>
                      )}
                    </div>
                  </div>
                  <Badge className={getPriorityColor(feature.priority)}>
                    {feature.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Planned Features */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Sparkles className="w-6 h-6" />
              Planned Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {plannedFeatures.map((feature) => (
                <div 
                  key={`planned-${feature.name}`}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-blue-50/30 border border-blue-100"
                >
                  <div className="flex items-start gap-3">
                    <Circle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-900">{feature.name}</p>
                      {feature.note && (
                        <p className="text-sm text-blue-700/60">{feature.note}</p>
                      )}
                    </div>
                  </div>
                  <Badge className={getPriorityColor(feature.priority)}>
                    {feature.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer note */}
        <div className="text-center mt-8 space-y-3">
          <p className="text-muted-foreground text-sm">
            Last updated: March 2026
          </p>
          <Link to="/beta-feedback">
            <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
              <Sparkles className="w-4 h-4 mr-2" />
              Submit Feature Requests & Feedback
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeatureRoadmap;
